/**
 * AdminLlmPanel — Envío 5: pestaña «Motor de IA» del admin.
 *
 * Elige proveedor + modelo (Anthropic u OpenRouter) por defecto, por función
 * (LMX / Prompt Master / Agente de workflows) y un respaldo opcional, sin
 * redeploy. Las keys viven en el .env del backend: acá sólo se ve si están
 * cargadas, nunca su valor. Botón «Probar» = una ronda real (centavos).
 * Uso y costo desde llm_usage (Paso 8 del Envío 3).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bot, KeyRound, Loader2, RefreshCw, Save, Play, CheckCircle2, XCircle,
  ChevronDown, Wrench, History,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminService } from '../../services/adminService';
import { usd } from './AdminCostsPanel';

const FEATURE_LABEL = {
  lmx: 'LMX (chat del canvas)',
  lmx_fast: 'LMX · respuestas rápidas (saludos y ayuda; vacío = mismo motor que LMX)',
  prompt_master: 'Prompt Master',
  workflow_agent: 'Agente de workflows',
};
const PROVIDER_LABEL = { anthropic: 'Anthropic', openrouter: 'OpenRouter' };
const KEY_ENV = { anthropic: 'ANTHROPIC_API_KEY', openrouter: 'OPENROUTER_API_KEY' };

const PERIODS = [
  { id: '7d', label: '7 días', days: 7 },
  { id: '30d', label: '30 días', days: 30 },
  { id: '90d', label: '90 días', days: 90 },
  { id: 'all', label: 'Todo', days: null },
];
const GROUPS = [
  { id: 'feature', label: 'Función' },
  { id: 'model', label: 'Modelo' },
  { id: 'provider', label: 'Proveedor' },
  { id: 'user', label: 'Usuario' },
  { id: 'day', label: 'Día' },
];

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es', {
    day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

// ── Selector de motor (proveedor + modelo con buscador) ────────────────────

function EnginePicker({ slot, value, onChange, onTest, testing, result,
                        inheritLabel, toolsOnly, keysOk }) {
  const [models, setModels] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrap = useRef(null);
  const provider = value?.provider || 'anthropic';
  const model = value?.model || '';

  useEffect(() => {
    let alive = true;
    if (!value) { setModels([]); return undefined; }
    adminService.listLlmModels(provider, toolsOnly)
      .then((res) => { if (alive) setModels(res.models || []); })
      .catch(() => { if (alive) setModels([]); });
    return () => { alive = false; };
  }, [provider, toolsOnly, value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (value === null) {
    // «Usar el motor por defecto»
    return (
      <div className="admin-llm-slot" data-testid={`llm-slot-${slot}`}>
        <button
          className="admin-llm-inherit"
          onClick={() => onChange({ provider: 'anthropic', model: '' })}
          data-testid={`llm-slot-${slot}-custom`}
        >
          {inheritLabel} · <u>usar uno propio</u>
        </button>
      </div>
    );
  }

  const filtered = models.filter((m) =>
    !search || m.id.toLowerCase().includes(search.toLowerCase())
    || (m.name || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="admin-llm-slot" data-testid={`llm-slot-${slot}`}>
      <div className="admin-llm-engine">
        <select
          value={provider}
          onChange={(e) => onChange({ provider: e.target.value, model: '' })}
          data-testid={`llm-slot-${slot}-provider`}
        >
          {Object.entries(PROVIDER_LABEL).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>

        <div className="admin-llm-combo" ref={wrap}>
          <input
            type="text"
            placeholder="modelo (buscá o escribí el id)"
            value={open ? search : model}
            onFocus={() => { setOpen(true); setSearch(model); }}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
              onChange({ provider, model: e.target.value }); // OpenRouter: id a mano
            }}
            data-testid={`llm-slot-${slot}-model`}
          />
          <button
            type="button"
            className="admin-costs-expand"
            onClick={() => setOpen((o) => !o)}
            data-testid={`llm-slot-${slot}-toggle`}
          >
            <ChevronDown size={13} />
          </button>
          {open && (
            <div className="admin-llm-combo-list" data-testid={`llm-slot-${slot}-list`}>
              {filtered.slice(0, 60).map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="admin-llm-combo-item"
                  onClick={() => {
                    onChange({ provider, model: m.id });
                    setOpen(false);
                    setSearch(m.id);
                  }}
                  data-testid={`llm-slot-${slot}-opt-${m.id}`}
                >
                  <span className="mono">{m.id}</span>
                  {m.supports_tools && (
                    <em className="admin-llm-tools"><Wrench size={10} /> tools</em>
                  )}
                  {m.price_in_mtok != null && (
                    <em>${m.price_in_mtok}/{m.price_out_mtok} Mtok</em>
                  )}
                </button>
              ))}
              {!filtered.length && (
                <div className="admin-costs-note" style={{ padding: 8 }}>
                  Sin coincidencias — podés escribir el id a mano.
                </div>
              )}
            </div>
          )}
        </div>

        {onChange && slot !== 'default' && (
          <button
            type="button"
            className="admin-costs-expand"
            title="Volver al motor por defecto"
            onClick={() => onChange(null)}
            data-testid={`llm-slot-${slot}-inherit`}
          >
            <XCircle size={14} />
          </button>
        )}

        <button
          type="button"
          className="admin-llm-test"
          disabled={!model || testing || keysOk === false}
          onClick={() => onTest({ provider, model }, toolsOnly)}
          data-testid={`llm-slot-${slot}-test`}
          title={keysOk === false ? `Falta ${KEY_ENV[provider]} en el .env` : 'Prueba real (gasta centavos)'}
        >
          {testing ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
          Probar
        </button>
      </div>

      {result && (
        <div className={`admin-llm-test-result ${result.success ? 'ok' : 'fail'}`}
             data-testid={`llm-slot-${slot}-result`}>
          {result.success ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
          <span>
            {result.success
              ? <>
                  {result.tool_called
                    ? <>llamó «{result.tool_called.name}» <CheckCircle2 size={10} /></>
                    : result.supports_tools_ok === false
                      ? <>NO llamó la herramienta — <b>este modelo no sirve para LMX</b></>
                      : null}
                  {' '}· {result.latency_ms} ms
                  · {result.usage?.input_tokens ?? 0}+{result.usage?.output_tokens ?? 0} tok
                  · {usd(result.cost_usd, 6)}
                  {result.text ? <> · “{result.text.slice(0, 90)}”</> : null}
                </>
              : result.error}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Panel ──────────────────────────────────────────────────────────────────

export default function AdminLlmPanel() {
  const [cfg, setCfg] = useState(null);
  const [keys, setKeys] = useState({});
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);       // slot id
  const [results, setResults] = useState({});         // slot -> resultado
  const [usage, setUsage] = useState([]);
  const [group, setGroup] = useState('feature');
  const [period, setPeriod] = useState('30d');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.getLlmConfig();
      setCfg(res.config);
      setKeys(res.keys || {});
      setMeta(res.meta || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar la config del motor');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsage = useCallback(async () => {
    try {
      const p = PERIODS.find((x) => x.id === period) || PERIODS[1];
      const from = p.days
        ? new Date(Date.now() - p.days * 86400000).toISOString()
        : '2020-01-01T00:00:00Z';
      const res = await adminService.getLlmUsage({ group, from });
      setUsage(res.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar el uso');
    }
  }, [group, period]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUsage(); }, [loadUsage]);

  const runTest = async (slot, engine, withTools) => {
    setTesting(slot);
    try {
      const res = await adminService.testLlmEngine({
        provider: engine.provider, model: engine.model, with_tools: !!withTools,
      });
      setResults((r) => ({ ...r, [slot]: res }));
    } catch (e) {
      setResults((r) => ({
        ...r,
        [slot]: { success: false, error: e.response?.data?.detail || e.message },
      }));
    } finally {
      setTesting(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = {
        ...cfg,
        max_tokens: Number(cfg.max_tokens),
        timeout_s: Number(cfg.timeout_s),
      };
      const res = await adminService.saveLlmConfig(clean);
      setCfg(res.config);
      (res.warnings || []).forEach((w) => toast.warning(w));
      toast.success('Motor guardado');
      load();
    } catch (e) {
      // Los 400 del backend se muestran tal cual (validación de plata).
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !cfg) {
    return (
      <div className="admin-costs-loading" data-testid="llm-loading">
        <Loader2 size={18} className="animate-spin" /> Cargando el motor de IA…
      </div>
    );
  }
  if (!cfg) return null;

  const patchFeature = (f, v) =>
    setCfg((c) => ({ ...c, features: { ...c.features, [f]: v } }));

  return (
    <div className="admin-llm" data-testid="admin-llm-panel">
      {/* Keys: sólo si están cargadas, NUNCA el valor */}
      <div className="admin-llm-keys" data-testid="llm-keys">
        <KeyRound size={13} />
        {Object.entries(PROVIDER_LABEL).map(([id, label]) => (
          <span key={id} className={`admin-llm-key ${keys[id] ? 'ok' : 'missing'}`}
                data-testid={`llm-key-${id}`}>
            {label} {keys[id]
              ? <CheckCircle2 size={12} />
              : <XCircle size={12} />}
            {!keys[id] && <em>Cargá {KEY_ENV[id]} en el .env del backend</em>}
          </span>
        ))}
      </div>

      <div className="admin-llm-section" data-testid="llm-default">
        <h4><Bot size={13} /> Motor por defecto</h4>
        <EnginePicker
          slot="default"
          value={cfg.default}
          onChange={(v) => setCfg((c) => ({ ...c, default: v }))}
          onTest={(eng) => runTest('default', eng, false)}
          testing={testing === 'default'}
          result={results.default}
          keysOk={keys[cfg.default?.provider]}
        />
      </div>

      <div className="admin-llm-section" data-testid="llm-features">
        <h4>Por función</h4>
        {Object.keys(cfg.features || {}).map((f) => (
          <div key={f} className="admin-llm-feature" data-testid={`llm-feature-${f}`}>
            <span className="admin-llm-feature-name">{FEATURE_LABEL[f] || f}</span>
            <EnginePicker
              slot={f}
              value={cfg.features[f]}
              onChange={(v) => patchFeature(f, v)}
              onTest={(eng) => runTest(f, eng, f === 'lmx')}
              testing={testing === f}
              result={results[f]}
              inheritLabel="Usar el motor por defecto"
              toolsOnly={f === 'lmx'}   // LMX no sirve sin tool calling
              keysOk={cfg.features[f] ? keys[cfg.features[f].provider] : true}
            />
          </div>
        ))}
      </div>

      <div className="admin-llm-section" data-testid="llm-fallback">
        <h4>Respaldo (si el principal falla antes de responder)</h4>
        <EnginePicker
          slot="fallback"
          value={cfg.fallback}
          onChange={(v) => setCfg((c) => ({ ...c, fallback: v }))}
          onTest={(eng) => runTest('fallback', eng, false)}
          testing={testing === 'fallback'}
          result={results.fallback}
          inheritLabel="Sin respaldo"
          keysOk={cfg.fallback ? keys[cfg.fallback.provider] : true}
        />
      </div>

      <div className="admin-llm-section admin-dp-fields" data-testid="llm-limits">
        <label className="admin-dp-field">
          <span>max_tokens</span>
          <input type="number" min="256" max="32000" step="256"
                 value={cfg.max_tokens}
                 onChange={(e) => setCfg((c) => ({ ...c, max_tokens: e.target.value }))}
                 data-testid="llm-max-tokens" />
        </label>
        <label className="admin-dp-field">
          <span>timeout (s)</span>
          <input type="number" min="10" max="600" step="10"
                 value={cfg.timeout_s}
                 onChange={(e) => setCfg((c) => ({ ...c, timeout_s: e.target.value }))}
                 data-testid="llm-timeout" />
        </label>
        <div className="admin-dp-actions">
          <button onClick={save} disabled={saving} data-testid="llm-save">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Guardar
          </button>
          <button className="admin-refresh" onClick={load} data-testid="llm-reload">
            <RefreshCw size={13} /> Descartar
          </button>
          <span className="admin-costs-note" data-testid="llm-meta">
            <History size={11} /> Último cambio: {meta.updated_by || '—'} · {fmtWhen(meta.updated_at)}
          </span>
        </div>
      </div>

      <div className="admin-llm-section" data-testid="llm-usage">
        <div className="admin-costs-grouped-head">
          <h4>Uso y costo</h4>
          <div className="admin-costs-periods">
            {PERIODS.map((p) => (
              <button key={p.id} className={period === p.id ? 'active' : ''}
                      onClick={() => setPeriod(p.id)}
                      data-testid={`llm-period-${p.id}`}>{p.label}</button>
            ))}
            <span className="admin-costs-dims">
              {GROUPS.map((g) => (
                <button key={g.id} className={group === g.id ? 'active' : ''}
                        onClick={() => setGroup(g.id)}
                        data-testid={`llm-group-${g.id}`}>{g.label}</button>
              ))}
            </span>
          </div>
        </div>
        <table className="admin-costs-table" data-testid="llm-usage-table">
          <thead>
            <tr>
              <th>{GROUPS.find((g) => g.id === group)?.label}</th>
              <th>Llamadas</th><th>Errores</th><th>Tokens entrada</th>
              <th>Tokens salida</th><th>Costo USD</th><th>Sin precio</th><th>Latencia prom.</th>
            </tr>
          </thead>
          <tbody>
            {usage.map((r) => (
              <tr key={r.group_key} data-testid={`llm-usage-row-${r.group_key}`}>
                <td className="mono">{r.group_key}</td>
                <td>{r.calls}</td>
                <td className={Number(r.errors) > 0 ? 'neg' : ''}>{r.errors}</td>
                <td>{Number(r.input_tokens || 0).toLocaleString('es')}</td>
                <td>{Number(r.output_tokens || 0).toLocaleString('es')}</td>
                <td>{usd(r.cost_total)}</td>
                <td>{r.calls_without_price > 0 ? r.calls_without_price : '—'}</td>
                <td>{r.avg_latency_ms ? `${Math.round(r.avg_latency_ms)} ms` : '—'}</td>
              </tr>
            ))}
            {!usage.length && <tr><td colSpan={8}>Sin uso en el período</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
